"use client"
import { useRef, useState, useEffect } from "react"
import type { FC } from "react"
import * as htmlToImage from "html-to-image"
import { jsPDF } from "jspdf"
import { motion, AnimatePresence } from "framer-motion"
import {
  ChevronLeft,
  ChevronRight,
  Download,
  RotateCcw,
  Heart,
  Users,
  Palette,
  FileText,
  Camera,
  Type,
  Sparkles,
  Paintbrush,
  HandHeart, // Nama ikon yang benar
  Infinity,
  Layers,
  Frame,
  Instagram,
  Clapperboard,
  X // Ikon untuk menutup alert
} from "lucide-react"

// --- TYPESCRIPT INTERFACES ---
interface PersonState {
  name: string
  mbti: string
  profilePic: string | null
  enneagram: string
  instagram: string
  tiktok: string
}

interface CoupleInfo {
  coupleName: string
  motto: string
}

interface Gradient {
  name:string
  colors: [string, string, string]
}

// --- DATA & CONSTANTS ---
const mbtiColors: { [key: string]: string } = {
  defender: "#3b82f6",
  thinker: "#8b5cf6",
  explorer: "#f59e0b",
  diplomat: "#22c55e",
};

const availableFonts: { name: string, value: string }[] = [
  { name: "Inter", value: "Inter, sans-serif" },
  { name: "Merriweather", value: "Merriweather, serif" },
  { name: "Playfair Display", value: "'Playfair Display', serif" },
  { name: "Poppins", value: "Poppins, sans-serif" },
  { name: "Dancing Script", value: "'Dancing Script', cursive" },
  { name: "Lobster", value: "Lobster, cursive" },
  { name: "Oswald", value: "Oswald, sans-serif" },
  { name: "Cinzel Decorative", value: "'Cinzel Decorative', cursive" },
];

const suggestedGradients: Gradient[] = [
    { name: "Sunset Kiss", colors: ["#FF7E5F", "#FEB47B", "#FFDAB9"] },
    { name: "Oceanic Dream", colors: ["#2E3192", "#1BFFFF", "#C3D4E0"] },
    { name: "Forest Whisper", colors: ["#0F2027", "#203A43", "#2C5364"] },
    { name: "Cosmic Dance", colors: ["#1D2B64", "#F8CDDA", "#C3CFE2"] },
    { name: "Ruby & Gold", colors: ["#D4145A", "#FBB03B", "#FFD700"] },
    { name: "Mint & Lavender", colors: ["#A1FFCE", "#FAFFD1", "#E0C3FC"] },
    { name: "Midnight Bloom", colors: ["#000428", "#004e92", "#4a00e0"] },
    { name: "Day & Night", colors: ["#F2C94C", "#274046", "#000000"] },
    { name: "Sakura Path", colors: ["#ffdde1", "#ee9ca7", "#FBD3E9"] },
    { name: "Royal Indigo", colors: ["#4776E6", "#8E54E9", "#3A3897"] },
    { name: "Vintage Paper", colors: ["#F3E9D2", "#C8C6A7", "#A4A4A4"] },
    { name: "Cyberpunk City", colors: ["#000000", "#4A00E0", "#8E2DE2"] },
    { name: "Pastel Sorbet", colors: ["#f8b195", "#f67280", "#c06c84"] },
];

const FORM_STEPS = [
  { id: 1, title: "Pilih Layout", description: "Pilih gaya desain kartu Anda", icon: <Layers /> },
  { id: 2, title: "Info Person 1", description: "Masukkan detail untuk orang pertama", icon: <Type /> },
  { id: 3, title: "Info Person 2", description: "Masukkan detail untuk orang kedua", icon: <Type /> },
  { id: 4, title: "Detail Pasangan", description: "Nama pasangan, motto, dan simbol penyatu", icon: <Users /> },
  { id: 5, title: "Desain Kartu", description: "Kustomisasi gradien, font, dan bingkai", icon: <Palette /> },
  { id: 6, title: "Final & Unduh", description: "Ekspor kartu pasangan Anda", icon: <Download /> },
];

// --- MAIN COMPONENT ---
const CoupleMBTICard: FC = () => {
  const cardRef = useRef<HTMLDivElement>(null)

  // --- STATE MANAGEMENT ---
  const [currentStep, setCurrentStep] = useState(1)

  const initialPersonState: PersonState = { name: "", mbti: "", profilePic: null, enneagram: "", instagram: "", tiktok: "" };
  const [person1, setPerson1] = useState<PersonState>({ ...initialPersonState, name: "person" });
  const [person2, setPerson2] = useState<PersonState>({ ...initialPersonState, name: "Fulanah" });
  const [coupleInfo, setCoupleInfo] = useState<CoupleInfo>({
    coupleName: "The Power Duo",
    motto: "Two minds, one journey.",
  })
  
  const [layout, setLayout] = useState<'layout1' | 'layout2'>('layout1');
  const [centerSymbol, setCenterSymbol] = useState<string>('Heart');
  const [heartColor, setHeartColor] = useState<string>('#FFFFFF');
  const [showFrameL1, setShowFrameL1] = useState<boolean>(true);
  const [frameStyleL1, setFrameStyleL1] = useState<string>('simple');

  // Design State
  const [activeGradient, setActiveGradient] = useState<Gradient>(suggestedGradients[0]);
  const [useCustomGradient, setUseCustomGradient] = useState(false);
  const [customGradient, setCustomGradient] = useState<[string, string, string]>(["#6DD5FA", "#FFFFFF", "#FF758C"]);
  const [customFont, setCustomFont] = useState(availableFonts[0].value)
  const [textColor, setTextColor] = useState("#FFFFFF")

  // Alert State
  const [showDesktopAlert, setShowDesktopAlert] = useState(false);

  // --- SIDE EFFECTS ---
  useEffect(() => {
      if (typeof window !== 'undefined' && window.innerWidth < 768) {
          setShowDesktopAlert(true);
      }
  }, []);

  // --- HELPER FUNCTIONS ---
  const mbtiCategory = (mbti: string): string => {
    const type = mbti.toUpperCase();
    if (["ISFJ", "ISTJ", "ESFJ", "ESTJ"].includes(type)) return "defender";
    if (["INTJ", "INTP", "ENTJ", "ENTP"].includes(type)) return "thinker";
    if (["ISTP", "ISFP", "ESTP", "ESFP"].includes(type)) return "explorer";
    if (["INFJ", "INFP", "ENFJ", "ENFP"].includes(type)) return "diplomat";
    return "diplomat";
  };

   const handlePersonChange = (personNum: 1 | 2, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, files } = e.target as HTMLInputElement;
    const updater = personNum === 1 ? setPerson1 : setPerson2;

    if (files && files[0]) {
      updater((prev) => ({ ...prev, profilePic: URL.createObjectURL(files[0]) }))
    } else {
      updater((prev) => ({ ...prev, [name]: value }))
    }
  };

  const handleCoupleInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCoupleInfo((prev) => ({ ...prev, [name]: value }))
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 2: return person1.name.trim() !== "" && person1.mbti !== "";
      case 3: return person2.name.trim() !== "" && person2.mbti !== "";
      case 4: return coupleInfo.coupleName.trim() !== "";
      default: return true;
    }
  };

  const nextStep = () => {
    if (currentStep < FORM_STEPS.length && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const downloadCard = async (format: 'png' | 'pdf') => {
      if (!cardRef.current) return;
      const pixelRatio = 2;
      const cardWidth = layout === 'layout1' ? 600 : 700;
      const cardHeight = layout === 'layout1' ? 350 : 450;
      const fileName = `${coupleInfo.coupleName.replace(/\s/g, '_')}_Card`;

      try {
          const dataUrl = await htmlToImage.toPng(cardRef.current, {
              pixelRatio, width: cardWidth, height: cardHeight,
              style: { transform: 'scale(1)', fontFamily: customFont },
          });
          if (format === 'png') {
              const link = document.createElement("a");
              link.download = `${fileName}.png`; link.href = dataUrl; link.click();
          } else {
              const pdf = new jsPDF({
                  orientation: "landscape", unit: "px", format: [cardWidth, cardHeight],
              });
              pdf.addImage(dataUrl, "PNG", 0, 0, cardWidth, cardHeight);
              pdf.save(`${fileName}.pdf`);
          }
      } catch (error) {
          console.error("Download failed:", error);
          alert("Download failed. Coba refresh halaman dan ulangi.");
      }
  };
  
  // --- DYNAMIC STYLES & COMPONENTS ---
  const finalGradient = useCustomGradient ? customGradient : activeGradient.colors;
  const cardBackgroundStyle = {
    background: `linear-gradient(135deg, ${finalGradient[0]}, ${finalGradient[1]}, ${finalGradient[2]})`,
    fontFamily: customFont, color: textColor
  };

  const SymbolIcon = ({ size }: { size: number }) => {
    const props = { size, color: centerSymbol === 'Heart' ? heartColor : textColor, className: "drop-shadow-lg" };
    if (centerSymbol === 'Heart') return <Heart {...props} fill={heartColor} />;
    if (centerSymbol === 'Friends') return <Users {...props} />;
    if (centerSymbol === 'Partners') return <HandHeart {...props} />;
    if (centerSymbol === 'Infinity') return <Infinity {...props} />;
    return <Heart {...props} fill={heartColor} />;
  };

  // --- RENDER FUNCTIONS ---
  const renderDesktopAlert = () => (
    <AnimatePresence>
      {showDesktopAlert && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-4 left-4 right-4 z-50 flex justify-center"
        >
          <div className="bg-yellow-400 border-4 border-dashed border-black text-black p-4 sm:p-6 rounded-lg shadow-2xl max-w-lg w-full transform -rotate-1 relative">
            <button
                onClick={() => setShowDesktopAlert(false)}
                className="absolute -top-3 -right-3 bg-black text-white rounded-full p-1 hover:scale-110 transition-transform"
            >
                <X size={16}/>
            </button>
            <h3 className="text-xl sm:text-2xl font-bold font-mono mb-2">WADUH, LAYAR KECIL DETECTED!</h3>
            <p className="text-sm sm:text-base mb-3">
              Situs ini paling cakep di <strong> mode dekstop</strong>. Kalau di HP, tampilannya agak maksa.
              Bukannya apa-apa, developernya aja yang <strong className="text-red-600 underline">males banget</strong> ngurusin scaling. Maklum, proyek iseng. 😬. Silahkan aktifkan mode dekstop di browser kesayangan kalian!!
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const renderLayout1 = () => (
      <div 
        className="w-[600px] h-[350px] rounded-2xl shadow-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden transition-all duration-500"
        style={cardBackgroundStyle}
      >
        {showFrameL1 && (
            <div className={`absolute inset-0 pointer-events-none border-4 rounded-2xl ${frameStyleL1 === 'simple' ? 'border-white/50' : 'border-transparent'}`}>
                {frameStyleL1 === 'ornate' && <div className="absolute inset-2 border-2 border-dashed border-white/40 rounded-xl"></div>}
            </div>
        )}
        <Sparkles className="absolute top-4 right-4 opacity-30" size={32}/>
        <h2 className="text-3xl font-bold tracking-wider mb-4 drop-shadow-lg">{coupleInfo.coupleName}</h2>
        <div className="flex justify-around items-center w-full">
            <div className="flex flex-col items-center w-5/12 text-center space-y-3">
              <div className="relative w-32 h-32 rounded-full border-4 border-white/30 shadow-lg bg-black/10 flex items-center justify-center">
                  {person1.profilePic ? <img src={person1.profilePic} alt={person1.name} className="w-full h-full object-cover rounded-full"/> : <div className="text-6xl opacity-70">👤</div>}
              </div>
              <h3 className="text-2xl font-bold drop-shadow-md">{person1.name || "Person 1"}</h3>
              <p className="text-xl font-semibold tracking-widest px-4 py-1 rounded-full text-white" style={{backgroundColor: mbtiColors[mbtiCategory(person1.mbti)]}}>
                  {person1.mbti || "MBTI"}
              </p>
            </div>
            <SymbolIcon size={48} />
            <div className="flex flex-col items-center w-5/12 text-center space-y-3">
              <div className="relative w-32 h-32 rounded-full border-4 border-white/30 shadow-lg bg-black/10 flex items-center justify-center">
                  {person2.profilePic ? <img src={person2.profilePic} alt={person2.name} className="w-full h-full object-cover rounded-full"/> : <div className="text-6xl opacity-70">👤</div>}
              </div>
              <h3 className="text-2xl font-bold drop-shadow-md">{person2.name || "Person 2"}</h3>
              <p className="text-xl font-semibold tracking-widest px-4 py-1 rounded-full text-white" style={{backgroundColor: mbtiColors[mbtiCategory(person2.mbti)]}}>
                  {person2.mbti || "MBTI"}
              </p>
            </div>
        </div>
        <p className="text-center italic mt-6 text-sm opacity-90">"{coupleInfo.motto}"</p>
      </div>
  );

  const renderLayout2 = () => (
      <div 
        className="w-[700px] h-[450px] shadow-2xl p-2 relative flex flex-col overflow-hidden"
        style={{...cardBackgroundStyle, borderRadius: '10px' }}
      >
        <div className="w-full h-full border-2 border-white/50 p-2 flex flex-col" style={{borderRadius: '8px'}}>
            <div className="w-full h-full border border-white/30 p-4 flex flex-col" style={{borderRadius: '5px'}}>
                <div className="text-center w-full mb-4 pb-2 border-b-2 border-white/40" style={{fontFamily: "'Cinzel Decorative', cursive"}}>
                    <h2 className="text-4xl font-bold tracking-widest">{coupleInfo.coupleName}</h2>
                </div>
                <div className="flex-grow flex justify-around items-center w-full">
                    <div className="flex-1 flex flex-col items-center text-center space-y-2 px-2">
                        <div className="w-32 h-32 p-1 border-2 border-white/50" style={{clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'}}>
                            <div className="w-full h-full bg-black/20 flex items-center justify-center" style={{clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'}}>
                                {person1.profilePic ? <img src={person1.profilePic} alt={person1.name} className="w-full h-full object-cover"/> : <div className="text-6xl opacity-70">👤</div>}
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold drop-shadow-md" style={{fontFamily: "'Playfair Display', serif"}}>{person1.name || "Person 1"}</h3>
                        <div className="flex gap-2 font-semibold">
                            <p className="px-3 py-1 bg-white/20 rounded">{person1.mbti || "MBTI"}</p>
                            <p className="px-3 py-1 bg-white/20 rounded">{person1.enneagram || "Ennea"}</p>
                        </div>
                        <div className="flex gap-3 text-xs pt-1">
                            {person1.instagram && <div className="flex items-center gap-1"><Instagram size={14}/> {person1.instagram}</div>}
                            {person1.tiktok && <div className="flex items-center gap-1"><Clapperboard size={14}/> {person1.tiktok}</div>}
                        </div>
                    </div>
                    <SymbolIcon size={52}/>
                    <div className="flex-1 flex flex-col items-center text-center space-y-2 px-2">
                        <div className="w-32 h-32 p-1 border-2 border-white/50" style={{clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'}}>
                             <div className="w-full h-full bg-black/20 flex items-center justify-center" style={{clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'}}>
                                {person2.profilePic ? <img src={person2.profilePic} alt={person2.name} className="w-full h-full object-cover"/> : <div className="text-6xl opacity-70">👤</div>}
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold drop-shadow-md" style={{fontFamily: "'Playfair Display', serif"}}>{person2.name || "Person 2"}</h3>
                        <div className="flex gap-2 font-semibold">
                            <p className="px-3 py-1 bg-white/20 rounded">{person2.mbti || "MBTI"}</p>
                            <p className="px-3 py-1 bg-white/20 rounded">{person2.enneagram || "Ennea"}</p>
                        </div>
                         <div className="flex gap-3 text-xs pt-1">
                            {person2.instagram && <div className="flex items-center gap-1"><Instagram size={14}/> {person2.instagram}</div>}
                            {person2.tiktok && <div className="flex items-center gap-1"><Clapperboard size={14}/> {person2.tiktok}</div>}
                        </div>
                    </div>
                </div>
                <div className="text-center w-full mt-4 pt-2 border-t-2 border-white/40">
                    <p className="italic text-sm opacity-90">"{coupleInfo.motto}"</p>
                </div>
            </div>
        </div>
      </div>
  );

  const renderStepContent = () => {
    const inputStyle = "border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white/80 text-gray-800";
    const labelStyle = "block text-sm font-medium text-gray-700 mb-1";
    const mbtiOptions = ["INFJ", "INFP", "INTJ", "INTP", "ENFJ", "ENFP", "ISTJ", "ISFJ", "ESTJ", "ESFJ", "ISTP", "ISFP", "ESTP", "ESFP", "ENTJ", "ENTP"];

    switch (currentStep) {
        case 1: return(
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div onClick={() => setLayout('layout1')} className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${layout === 'layout1' ? 'border-blue-600 ring-2 ring-blue-300' : 'border-gray-200 hover:border-blue-400'}`}>
                        <div className="w-full h-24 bg-gradient-to-br from-blue-400 to-purple-500 rounded-md flex items-center justify-center text-white font-bold text-lg mb-2">Modern & Clean</div>
                        <h4 className="font-semibold text-center text-gray-800">Layout 1</h4>
                    </div>
                    <div onClick={() => setLayout('layout2')} className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${layout === 'layout2' ? 'border-blue-600 ring-2 ring-blue-300' : 'border-gray-200 hover:border-blue-400'}`}>
                        <div className="w-full h-24 bg-gradient-to-br from-gray-700 via-gray-900 to-black rounded-md flex items-center justify-center text-white font-bold text-lg mb-2">Classic & Rich</div>
                        <h4 className="font-semibold text-center text-gray-800">Layout 2</h4>
                    </div>
                </div>
            </motion.div>
        );
        case 2:
        case 3:
            const person = currentStep === 2 ? person1 : person2;
            const handler = currentStep === 2 ? (e: any) => handlePersonChange(1, e) : (e: any) => handlePersonChange(2, e);
            const personNum = currentStep === 2 ? 1 : 2;
            return (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  <div>
                      <label className={labelStyle}>Nama Person {personNum}</label>
                      <input name="name" placeholder="Masukkan nama" value={person.name} onChange={handler} className={inputStyle} />
                  </div>
                  <div>
                      <label className={labelStyle}>Tipe MBTI</label>
                      <select name="mbti" value={person.mbti} onChange={handler} className={inputStyle}>
                          <option value="">Pilih Tipe MBTI</option>
                          {mbtiOptions.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                  </div>
                  {layout === 'layout2' && (<>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <label className={labelStyle}>Enneagram (Opsional)</label>
                        <input name="enneagram" placeholder="cth: 9w1" value={person.enneagram} onChange={handler} className={inputStyle} />
                    </motion.div>
                     <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <label className={labelStyle}>Instagram (tanpa @, Opsional)</label>
                        <input name="instagram" placeholder="cth: username" value={person.instagram} onChange={handler} className={inputStyle} />
                    </motion.div>
                     <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <label className={labelStyle}>TikTok (tanpa @, Opsional)</label>
                        <input name="tiktok" placeholder="cth: username" value={person.tiktok} onChange={handler} className={inputStyle} />
                    </motion.div>
                  </>)}
                  <div>
                      <label className={labelStyle}><Camera size={14} className="inline mr-1"/> Foto Profil (Opsional)</label>
                      <input name="profilePic" type="file" accept="image/*" onChange={handler} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"/>
                  </div>
              </motion.div>
            );
        case 4: return (
             <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div>
                    <label className={labelStyle}><Users size={14} className="inline mr-1"/> Nama Pasangan / Tim</label>
                    <input name="coupleName" placeholder="cth: The Adventurers" value={coupleInfo.coupleName} onChange={handleCoupleInfoChange} className={inputStyle} />
                </div>
                 <div>
                    <label className={labelStyle}><FileText size={14} className="inline mr-1"/> Motto Bersama</label>
                    <textarea name="motto" placeholder="Kutipan yang mendefinisikan Anda berdua" value={coupleInfo.motto} onChange={handleCoupleInfoChange} className={inputStyle + " h-24"} />
                </div>
                <div>
                    <label className={labelStyle}>Simbol Penengah</label>
                    <div className="flex justify-around items-center bg-gray-100 p-2 rounded-lg">
                        {(['Heart', 'Friends', 'Partners', 'Infinity'] as const).map(sym => (
                            <button key={sym} onClick={() => setCenterSymbol(sym)} className={`p-2 rounded-full transition-all text-gray-600 ${centerSymbol === sym ? 'bg-blue-500 text-white' : 'hover:bg-gray-200'}`}>
                                {sym === 'Heart' && <Heart />} {sym === 'Friends' && <Users />} {sym === 'Partners' && <HandHeart />} {sym === 'Infinity' && <Infinity />}
                            </button>
                        ))}
                    </div>
                    {centerSymbol === 'Heart' && (
                        <motion.div initial={{opacity:0}} animate={{opacity:1}} className="mt-2">
                           <label className={labelStyle}>Warna Hati</label>
                           <input type="color" value={heartColor} onChange={e => setHeartColor(e.target.value)} className="w-full h-10 border rounded-lg cursor-pointer"/>
                        </motion.div>
                    )}
                </div>
            </motion.div>
        );
        case 5: return (
             <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                 <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-3">1. Pilih Gradien</h4>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                        {suggestedGradients.map(g => (
                            <div key={g.name} onClick={() => { setActiveGradient(g); setUseCustomGradient(false); }} className={`cursor-pointer rounded-lg p-1 border-2 text-center text-[10px] transition-all text-gray-700 ${!useCustomGradient && activeGradient.name === g.name ? 'border-blue-600 ring-2 ring-blue-300' : 'border-gray-200 hover:border-blue-400'}`}>
                                <div className="h-10 w-full rounded-md mb-1" style={{background: `linear-gradient(135deg, ${g.colors[0]}, ${g.colors[1]})`}}></div> {g.name}
                            </div>
                        ))}
                         <div onClick={() => setUseCustomGradient(true)} className={`cursor-pointer rounded-lg p-1 border-2 text-center text-xs transition-all flex flex-col items-center justify-center text-gray-700 ${useCustomGradient ? 'border-blue-600 ring-2 ring-blue-300' : 'border-gray-200 hover:border-blue-400'}`}>
                            <Paintbrush size={20} className="mb-1 text-gray-500"/> Custom
                         </div>
                    </div>
                    {useCustomGradient && (
                        <motion.div initial={{opacity: 0, height: 0}} animate={{opacity: 1, height: 'auto'}} className="grid grid-cols-3 gap-3 mt-4">
                           <input type="color" value={customGradient[0]} onChange={e => setCustomGradient(p => [e.target.value, p[1], p[2]])} className="w-full h-10 rounded-lg cursor-pointer border-gray-300"/>
                           <input type="color" value={customGradient[1]} onChange={e => setCustomGradient(p => [p[0], e.target.value, p[2]])} className="w-full h-10 rounded-lg cursor-pointer border-gray-300"/>
                           <input type="color" value={customGradient[2]} onChange={e => setCustomGradient(p => [p[0], p[1], e.target.value])} className="w-full h-10 rounded-lg cursor-pointer border-gray-300"/>
                        </motion.div>
                    )}
                 </div>
                 <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">2. Font & Warna Teks</h4>
                     <select value={customFont} onChange={e => setCustomFont(e.target.value)} className={inputStyle + " mb-3"}>
                         {availableFonts.map(f => <option key={f.name} value={f.value} style={{fontFamily: f.value}}>{f.name}</option>)}
                     </select>
                     <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} className="w-full h-10 border rounded-lg cursor-pointer"/>
                 </div>
                 {layout === 'layout1' && (
                    <motion.div initial={{opacity:0}} animate={{opacity:1}} className="space-y-2">
                         <h4 className="text-md font-semibold text-gray-800">3. Opsi Bingkai (Layout 1)</h4>
                         <div className="flex items-center space-x-2">
                            <input type="checkbox" id="showFrame" checked={showFrameL1} onChange={e => setShowFrameL1(e.target.checked)} className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"/>
                            <label htmlFor="showFrame" className="text-gray-700">Tampilkan Bingkai</label>
                         </div>
                         {showFrameL1 && (
                            <select value={frameStyleL1} onChange={e => setFrameStyleL1(e.target.value)} className={inputStyle}>
                                <option value="simple">Simple</option>
                                <option value="ornate">Ornate</option>
                            </select>
                         )}
                    </motion.div>
                 )}
            </motion.div>
        );
        case 6: return (
             <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 text-center">
                 <h3 className="text-xl font-semibold text-gray-800">Kartu Anda Siap!</h3>
                 <div className="flex flex-col sm:flex-row gap-4 pt-4">
                     <button onClick={() => downloadCard('png')} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">Unduh PNG</button>
                     <button onClick={() => downloadCard('pdf')} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">Unduh PDF</button>
                 </div>
            </motion.div>
        );
        default: return null;
    }
  }

  return (
    <div className="min-h-screen w-full bg-gray-100 font-sans p-4 md:p-8" style={{background: 'linear-gradient(to bottom, #e0e7ff, #f3f4f6)'}}>
        {renderDesktopAlert()}
        <div className="max-w-7xl mx-auto">
            <header className="text-center mb-8 pt-16 sm:pt-0">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">Couple MBTI Card Generator</h1>
                <p className="text-gray-600 mt-2 px-2">Buat kartu identitas bersama yang indah untuk Anda dan pasangan atau sahabat.</p>
            </header>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-white/90 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-200 w-full">
                    <div className="mb-6 flex items-center space-x-3">
                        <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">{FORM_STEPS[currentStep-1].icon}</div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">{FORM_STEPS[currentStep - 1]?.title}</h2>
                            <p className="text-gray-600 text-sm">{FORM_STEPS[currentStep - 1]?.description}</p>
                        </div>
                    </div>
                    <AnimatePresence mode="wait">
                      <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                          {renderStepContent()}
                      </motion.div>
                    </AnimatePresence>
                    <div className="flex justify-between pt-6 mt-6 border-t">
                        <button onClick={prevStep} disabled={currentStep === 1} className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors disabled:bg-gray-200 disabled:text-gray-400 bg-gray-600 text-white hover:bg-gray-700">
                           <ChevronLeft size={16} /> <span>Sebelumnya</span>
                        </button>
                        <button onClick={nextStep} disabled={currentStep === FORM_STEPS.length || !isStepValid(currentStep)} className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors disabled:bg-gray-200 disabled:text-gray-400 bg-blue-600 text-white hover:bg-blue-700">
                            <span>{currentStep === FORM_STEPS.length ? "Selesai" : "Selanjutnya"}</span> <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
                <div className="flex flex-col items-center justify-start space-y-4 lg:sticky lg:top-8 w-full">
                     <h3 className="text-xl font-semibold text-gray-700">Pratinjau Langsung</h3>
                     <div className="w-full flex justify-center transform-gpu origin-top" style={{
                         transform: `scale(clamp(0.45, calc(100vw / ${layout === 'layout1' ? '650px' : '750px'}), 1))`
                     }}>
                        <div ref={cardRef}>
                            {layout === 'layout1' ? renderLayout1() : renderLayout2()}
                        </div>
                     </div>
                </div>
            </div>
        </div>
    </div>
  )
}

export default CoupleMBTICard;
